import { useState } from 'react'
import type { StatBucket } from '../api/public'

/**
 * A part-to-whole bar for an ORDERED distribution.
 *
 * # Why this is not a pie
 *
 * The buckets are an ordered scale, not a set of names: albatross → eagle →
 * birdie → par → bogey → double-or-worse runs from best to worst, and the
 * question a golfer asks of it is "how much of my round was par or better?".
 * That is a pivot, and a pie has no pivot — it has a starting angle, which is
 * arbitrary. A ring also makes the two questions people actually ask harder:
 * comparing adjacent slices (they are at different angles) and comparing two
 * players (two rings cannot be laid against each other).
 *
 * A full-width stacked bar keeps every bar 100% wide, so two players' bars line
 * up; the ordered fills keep best-to-worst reading left to right; and a rule at
 * the pivot answers the actual question in one glance.
 *
 * # The palette is computed, not chosen
 *
 * A diverging ramp: one hue per arm, intensity rising away from the middle,
 * with a near-neutral at the pivot. Every step was run through the data-viz
 * validator against this app's own surface — each arm as a one-hue ordinal ramp
 * (monotone lightness, ≥0.06 lightness gaps, light end clearing the surface),
 * and the adjacent pairs for colourblind separation and the normal-vision
 * floor. Light passes at ΔE 14.5 (protan) and 16.6 (normal); dark at 11.9 and
 * 17.2. The first green arm I tried failed the normal-vision floor at 13.3 and
 * was re-stepped rather than talked out of.
 *
 * Both modes are SELECTED — the dark steps are chosen against the dark surface,
 * not flipped from the light ones.
 *
 * # Identity is never colour alone
 *
 * Six fills is past the point where colour carries identity on its own, so
 * every segment is in the legend, wide segments are labelled in place, and the
 * full numbers stay in the table below the chart. A reader who cannot separate
 * two greens loses nothing.
 */
export function DistributionBar({
  buckets,
  total,
  palette,
  pivotAfter,
  pivotLabels,
  caption,
  compact,
}: {
  buckets: StatBucket[]
  total: number
  /** One fill per bucket, in bucket order. Validated — see the note above. */
  palette: string[]
  /** Draw the pivot rule after this bucket key. Omitted for a plain ramp. */
  pivotAfter?: string
  pivotLabels?: { before: string; after: string }
  caption: string
  /**
   * Just the bar: no pivot labels, readout or legend. For several bars laid
   * against each other, which share one legend rather than repeating it.
   */
  compact?: boolean
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  if (total === 0) return null

  // Shares are computed from the counts rather than read from the server's
  // rounded percentages, so the segments always add to the full width.
  const shares = buckets.map((b) => (b.count / total) * 100)
  // The running offset as a fold rather than a mutated closure: the lint rule
  // is right that reassigning across a render is a hazard, and the prefix sum
  // says what it means anyway.
  const offsets = shares.map((_, i) => shares.slice(0, i).reduce((a, b) => a + b, 0))

  const pivotIndex = pivotAfter ? buckets.findIndex((b) => b.key === pivotAfter) : -1
  const pivotAt = pivotIndex >= 0 ? offsets[pivotIndex] + shares[pivotIndex] : null
  const beforeShare = pivotAt ?? 0

  return (
    <figure
      className={`viz-root distribution${compact ? ' distribution-compact' : ''}`}
      data-testid={compact ? undefined : 'distribution-chart'}
    >
      {!compact && pivotAt !== null && pivotLabels && (
        <figcaption className="distribution-pivot-labels">
          <span>
            {pivotLabels.before} <strong>{beforeShare.toFixed(1)}%</strong>
          </span>
          <span>
            <strong>{(100 - beforeShare).toFixed(1)}%</strong> {pivotLabels.after}
          </span>
        </figcaption>
      )}

      <svg
        className="distribution-bar"
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        role="img"
        aria-label={caption}
      >
        {buckets.map((b, i) => {
          if (shares[i] === 0) return null
          // **A 2px surface gap separates touching segments**, not a stroke.
          // In a 100-unit viewBox scaled to the container, 0.35 units is about
          // 2px at the widths this renders at.
          const gap = i === buckets.length - 1 ? 0 : 0.35
          const width = Math.max(shares[i] - gap, 0.2)
          return (
            <rect
              key={b.key}
              x={offsets[i]}
              y={0}
              width={width}
              height={10}
              fill={palette[i] ?? palette[palette.length - 1]}
              opacity={hovered === null || hovered === i ? 1 : 0.45}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <title>{`${b.label}: ${b.count} of ${total} holes (${shares[i].toFixed(1)}%)`}</title>
            </rect>
          )
        })}
        {pivotAt !== null && (
          /* The pivot is the whole point of the form, so it is a real rule
             rather than a colour change a reader has to infer. */
          <line
            x1={pivotAt - 0.17}
            x2={pivotAt - 0.17}
            y1={-1}
            y2={11}
            className="distribution-pivot"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      {compact ? null : (
        <DistributionDetail
          buckets={buckets}
          palette={palette}
          total={total}
          shares={shares}
          hovered={hovered}
          caption={caption}
        />
      )}
    </figure>
  )
}

/** The readout and legend under a full-size bar. */
function DistributionDetail({
  buckets,
  palette,
  total,
  shares,
  hovered,
  caption,
}: {
  buckets: StatBucket[]
  palette: string[]
  total: number
  shares: number[]
  hovered: number | null
  caption: string
}) {
  const active = hovered !== null ? buckets[hovered] : null
  return (
    <>
      {/* The hover readout. One line, above the legend, so it never covers the
          mark the reader is pointing at. */}
      <p className="distribution-readout" role="status" data-testid="distribution-readout">
        {active ? (
          <>
            <span className="swatch" style={{ background: paletteAt(palette, hovered) }} />
            {active.label}: <strong>{active.count}</strong> of {total} holes (
            {shares[hovered!].toFixed(1)}%)
          </>
        ) : (
          <span className="note">{caption}</span>
        )}
      </p>

      <ul className="distribution-legend" data-testid="distribution-legend">
        {buckets.map((b, i) => (
          <li key={b.key}>
            {/* Text wears text tokens; the swatch beside it carries identity. */}
            <span className="swatch" style={{ background: paletteAt(palette, i) }} aria-hidden />
            {b.label}
            <span className="legend-count">{b.count}</span>
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * The legend alone, for bars drawn compact: the buckets' names and fills,
 * without counts, which differ bar to bar.
 */
export function DistributionLegend({
  buckets,
  palette,
}: {
  buckets: StatBucket[]
  palette: string[]
}) {
  return (
    <ul className="distribution-legend" data-testid="distribution-legend">
      {buckets.map((b, i) => (
        <li key={b.key}>
          <span className="swatch" style={{ background: paletteAt(palette, i) }} aria-hidden />
          {b.label}
        </li>
      ))}
    </ul>
  )
}

function paletteAt(palette: string[], i: number | null): string {
  if (i === null) return 'transparent'
  return palette[i] ?? palette[palette.length - 1]
}
